import Banner from '@/components/Banner';
import CourseCard from '@/components/CourseCard';
import NoResult from '@/components/NoResult';
import { RevealGroup, RevealItem } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { Course, CourseCategory } from '@/types';

type CourseDetail = Pick<
  Course,
  | 'id'
  | 'title'
  | 'slug'
  | 'description'
  | 'image_url'
  | 'rating_count'
  | 'review_count'
>;

const ShowCategory = ({
  category,
}: {
  category: CourseCategory<CourseDetail>;
}) => {
  return (
    <>
      <Banner
        image={category.image_url}
        alt={category.title}
        tagline="Category"
        title={category.title}
        description={category.description}
      >
        <ShareDialog
          url={window.location.href}
          title={category.title + ' - ' + import.meta.env.VITE_APP_NAME}
          content={category.description}
        />
      </Banner>

      <Band tone="surface">
        <SectionLayout space="lg">
          {/* Visually hidden: the Banner above already shows the category
              name as this page's h1, but nothing else sits between it and
              the course grid below, so screen readers and heading-order
              checks see a raw h1 -> h3 jump without a real h2 to land on. */}
          <Typography.H2 className="sr-only">{category.title}</Typography.H2>
          {/* "available" rather than "{n} courses" — category names already
              end in "Courses", so the count would repeat the word right
              below the heading. */}
          <Typography.Small>
            {category.courses.length} available
          </Typography.Small>

          {category.courses.length > 0 ? (
            <RevealGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {category.courses.map((course) => (
                <RevealItem key={course.id} className="flex">
                  <CourseCard category={category} course={course} />
                </RevealItem>
              ))}
            </RevealGroup>
          ) : (
            <NoResult kind="Courses" />
          )}
        </SectionLayout>
      </Band>
    </>
  );
};

export default ShowCategory;
